<script setup lang="ts">
import { useTopBarTitle } from '@/shared/composables/UseTopBarTitle'
import {
  getGetMatchingSimulationQueryKey,
  getGetMatchingSimulationStatisticsQueryKey,
  getListMatchingSimulationsQueryKey,
  useCancelMatchingSimulation,
  useDeleteMatchingSimulation,
  useDeleteMatchingSimulationScores,
  useGetMatchingSimulation,
  useGetMatchingSimulationStatus,
  useRunMatchingSimulation,
} from '@swipe4work/api-orval'
import { useToast as usePrimeToast } from 'primevue/usetoast'
import { useQueryClient } from '@tanstack/vue-query'
import { useDynamicDialog } from '@/shared/composables/UseDynamicDialog'
import RunSimulationDialog from '@/features/matching/dialogs/RunSimulationDialog.vue'
import SimulationDetailHeader from '@/features/matching/components/SimulationDetailHeader.vue'
import { useConfirm } from 'primevue/useconfirm'
import { useToast } from '@/shared/composables/UseToast'

definePage({
  path: '/matching/simulations/:id',
  name: '/matching/SimulationDetail',
  meta: {
    title: 'Simulatie',
    breadcrumbParent: '/matching/Simulations',
    permission: {
      resource: 'Simulations',
      level: 'Read',
    },
  },
})

const route = useRoute('/matching/SimulationDetail')
const router = useRouter()
const id = computed(() => Number((route.params as { id: string }).id))
const primeToast = usePrimeToast()
const toast = useToast()
const confirm = useConfirm()
const queryClient = useQueryClient()
const dialog = useDynamicDialog()

provide<ComputedRef<number>>('simulationId', id)

onBeforeMount(() => {
  if (route.name === '/matching/SimulationDetail') {
    router.replace({
      name: '/matching/SimulationDetail/overview' as any,
      params: { id: id.value },
    })
  }
})

watch(() => route.name, (name) => {
  if (name === '/matching/SimulationDetail') {
    router.replace({
      name: '/matching/SimulationDetail/overview' as any,
      params: { id: id.value },
    })
  }
})

const { data: simulationData } = useGetMatchingSimulation(id, {
  query: {
    staleTime: 60 * 1000,
    enabled: computed(() => id.value > 0),
  },
})

const simulation = computed(() => simulationData.value?.simulation ?? null)

// The topbar shows the simulation name as the last breadcrumb once loaded.
useTopBarTitle(() => simulation.value?.name)

// Used only to drive refetchInterval — avoids circular dependency with statusData
const isRunningFromSimulation = computed(() => simulation.value?.status === 'Running')

const { data: statusData } = useGetMatchingSimulationStatus(id, {
  query: {
    enabled: computed(() => id.value > 0),
    refetchInterval: computed(() => isRunningFromSimulation.value ? 3000 : 10000),
    staleTime: 0,
  },
})

// Reflects actual running state — catches externally started runs via status polling
const isSimulationRunning = computed(
  () => isRunningFromSimulation.value || statusData.value?.status === 'Running',
)

watch(statusData, (current, previous) => {
  if (previous?.status === 'Running' && current?.status !== 'Running') {
    queryClient.invalidateQueries({ queryKey: getGetMatchingSimulationQueryKey(id.value) })
    queryClient.invalidateQueries({ queryKey: getGetMatchingSimulationStatisticsQueryKey(id.value) })
    queryClient.invalidateQueries({ queryKey: getListMatchingSimulationsQueryKey() })

    if (current?.status === 'Completed') {
      primeToast.add({
        severity: 'success',
        summary: 'Klaar',
        detail: 'Simulatie is uitgevoerd.',
      })
    }
  }
})

// Elapsed duration ticker (while running)
const elapsedSeconds = ref(0)
let elapsedTimer: ReturnType<typeof setInterval> | null = null

watch(isSimulationRunning, (running) => {
  if (running) {
    const startedAt = simulation.value?.startedAt
    const startMs = startedAt ? new Date(startedAt).getTime() : Date.now()

    elapsedSeconds.value = Math.floor((Date.now() - startMs) / 1000)
    elapsedTimer = setInterval(() => {
      elapsedSeconds.value = Math.floor((Date.now() - startMs) / 1000)
    }, 1000)
  } else {
    if (elapsedTimer !== null) {
      clearInterval(elapsedTimer)
      elapsedTimer = null
    }
  }
}, { immediate: true })

onUnmounted(() => {
  if (elapsedTimer !== null) {
    clearInterval(elapsedTimer)
  }
})

function formatDuration(seconds: number): string {
  if (seconds < 60) { return `${seconds}s` }
  const m = Math.floor(seconds / 60)
  const s = seconds % 60

  return s > 0 ? `${m}m ${s}s` : `${m}m`
}

function onRun() {
  const current = simulation.value
  if (!current) {
    return
  }

  dialog.open(RunSimulationDialog, {
    props: {
      modal: true,
      closable: true,
      showHeader: false,
      dismissableMask: true,
    },
    data: {
      simulationId: current.id,
      simulationName: current.name,
      algorithmVersionId: current.algorithmVersionId,
      algorithmVersionName: current.algorithmVersionName,
      userSegmentId: current.userSegmentId ?? null,
      jobSegmentId: current.jobSegmentId ?? null,
      companySegmentId: current.companySegmentId ?? null,
      startInStreamingMode: isSimulationRunning.value,
      startedAt: current.startedAt ?? null,
    },
  })
}

const { mutate: deleteSimulation } = useDeleteMatchingSimulation()
const { mutate: deleteScores } = useDeleteMatchingSimulationScores()
const { mutate: cancelSimulation } = useCancelMatchingSimulation()
const { mutate: runSimulationMutate } = useRunMatchingSimulation()

function onCancel() {
  cancelSimulation({ id: id.value }, {
    onSuccess: async () => {
      toast.success('Geannuleerd', 'Annuleringsverzoek verstuurd.')
      await queryClient.invalidateQueries({ queryKey: getGetMatchingSimulationQueryKey(id.value) })
    },
    onError: () => {
      toast.error('Fout', 'Annuleren mislukt.')
    },
  })
}

function onDeleteScores() {
  confirm.require({
    message: `Weet je zeker dat je alle scores van "${simulation.value?.name}" wilt verwijderen? De simulatie zelf blijft behouden.`,
    header: 'Scores verwijderen',
    icon: 'fa-regular fa-eraser',
    acceptLabel: 'Verwijderen',
    acceptClass: 'p-button-danger',
    rejectLabel: 'Annuleren',
    accept: () => {
      deleteScores({ id: id.value }, {
        onSuccess: async (result) => {
          toast.success('Scores verwijderd', `${result?.deletedCount ?? 0} matchscores verwijderd.`)
          await queryClient.invalidateQueries({ queryKey: getGetMatchingSimulationQueryKey(id.value) })
          await queryClient.invalidateQueries({ queryKey: getListMatchingSimulationsQueryKey() })
        },
      })
    },
  })
}

function onDelete() {
  confirm.require({
    message: `Weet je zeker dat je simulatie "${simulation.value?.name}" wilt verwijderen? Dit verwijdert ook alle berekende matchscores.`,
    header: 'Simulatie verwijderen',
    icon: 'fa-regular fa-trash',
    acceptLabel: 'Verwijderen',
    acceptClass: 'p-button-danger',
    rejectLabel: 'Annuleren',
    accept: () => {
      deleteSimulation({ id: id.value }, {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: getListMatchingSimulationsQueryKey() })
          router.push({ name: '/matching/Simulations' as any })
        },
      })
    },
  })
}

function onRerun() {
  const current = simulation.value
  if (!current) { return }

  runSimulationMutate({ id: current.id }, {
    onSuccess: async () => {
      dialog.open(RunSimulationDialog, {
        props: {
          modal: true,
          closable: true,
          showHeader: false,
          dismissableMask: true,
        },
        data: {
          simulationId: current.id,
          simulationName: current.name,
          algorithmVersionId: current.algorithmVersionId,
          algorithmVersionName: current.algorithmVersionName,
          userSegmentId: current.userSegmentId ?? null,
          jobSegmentId: current.jobSegmentId ?? null,
          companySegmentId: current.companySegmentId ?? null,
          startInStreamingMode: true,
          startedAt: null,
        },
      })
      await queryClient.invalidateQueries({ queryKey: getGetMatchingSimulationQueryKey(id.value) })
      await queryClient.invalidateQueries({ queryKey: getListMatchingSimulationsQueryKey() })
    },
    onError: () => {
      toast.error('Fout', 'Heruitvoeren mislukt.')
    },
  })
}

const tabs = [
  {
    label: 'Overzicht',
    name: '/matching/SimulationDetail/overview',
  },
  {
    label: 'Match-scores',
    name: '/matching/SimulationDetail/match-scores',
  },
  {
    label: 'Kandidaten',
    name: '/matching/SimulationDetail/candidates',
  },
  {
    label: 'Vacatures',
    name: '/matching/SimulationDetail/jobs',
  },
]
</script>

<template>
  <div class="space-y-5">
    <SimulationDetailHeader
      :simulation="simulation"
      :is-running="isSimulationRunning"
      @run="onRun"
      @cancel="onCancel"
      @delete-scores="onDeleteScores"
      @delete="onDelete"
      @rerun="onRerun"
    />

    <!-- Progress bar while running -->
    <Card v-if="isSimulationRunning">
      <template #content>
        <div class="flex flex-col gap-1.5">
          <div class="flex items-center justify-between text-xs text-surface-400">
            <span><i class="fa-regular fa-clock mr-1" />{{ formatDuration(elapsedSeconds) }}</span>
            <span>{{ statusData?.progress ?? 0 }}%</span>
          </div>
          <ProgressBar :value="statusData?.progress ?? 0" style="height: 6px" />
        </div>
      </template>
    </Card>

    <Tabs :value="route.name" scrollable>
      <TabList>
        <router-link
          v-for="tab in tabs"
          :key="tab.name"
          :to="{ name: tab.name as any, params: { id } }"
        >
          <Tab :value="tab.name">
            {{ tab.label }}
          </Tab>
        </router-link>
      </TabList>
    </Tabs>

    <router-view v-slot="{ Component }">
      <component :is="Component" />
    </router-view>
  </div>
</template>
